
const libPictView = require('pict-view');
require('../approval-workflow/ApprovalWorkflow-Help.js'); // sets window.ApprovalWorkflowHelp for the (i) icons

/**
 * ApprovalWorkflowList — the #/ApprovalWorkflows landing.
 *
 * A card list of the customer's saved approval workflows (Configuration NamespaceKey 'ApprovalWorkflow'),
 * each showing its hash + state/transition counts and linking to the visual editor. "New workflow" seeds a
 * default graph. All data lives in ApprovalWorkflowProvider (AppData.ApprovalWorkflowEditor); this view
 * derives loading/empty/error slots and repaints declaratively.
 */
class ApprovalWorkflowList extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get provider()
	{
		return this.pict.providers.ApprovalWorkflow;
	}

	get modal()
	{
		return this.pict.views['Pict-Section-Modal'];
	}

	onBeforeRender(pRenderable)
	{
		const tmpState = this.provider ? this.provider.state : { List: [], ListLoading: false, ListError: '' };
		const tmpAppState = this.pict.AppData.ApprovalWorkflowEditor || {};
		// Conditional slots (single-element-array trick): loading / error / empty.
		tmpAppState.LoadingSlot = tmpState.ListLoading ? [ {} ] : [];
		tmpAppState.ErrorSlot = tmpState.ListError ? [ { Message: tmpState.ListError } ] : [];
		tmpAppState.EmptySlot = (!tmpState.ListLoading && !tmpState.ListError && tmpState.List.length === 0) ? [ {} ] : [];
		tmpAppState.CanEdit = this.provider ? this.provider.canEdit : false;
		tmpAppState.NewButtonSlot = tmpAppState.CanEdit ? [ {} ] : [];
		tmpAppState.WorkflowCount = tmpState.List.length;
		tmpAppState.CountSlot = (!tmpState.ListLoading && !tmpState.ListError && tmpState.List.length > 0) ? [ { Count: tmpState.List.length } ] : [];
		// Per-card Clone control (single-element-array trick): only for users who can edit.
		tmpState.List.forEach((pRow) =>
		{
			pRow.EncodedHash = encodeURIComponent(pRow.Hash);
			pRow.LevelSlot = pRow.LevelText ? [ { Text: this._escapeHTML(pRow.LevelText) } ] : [];
			pRow.CloneControlSlot = tmpAppState.CanEdit ? [ { Hash: this._escapeHTML(pRow.Hash) } ] : [];
		});
		const tmpProjectOnly = (tmpState.ProjectOnlyList || []).map((pRow) => ({ Hash: this._escapeHTML(pRow.Hash), ProjectName: this._escapeHTML(pRow.ProjectName), Href: `#/ApprovalWorkflow/${encodeURIComponent(pRow.Hash)}/Project/${pRow.IDProject}` }));
		tmpAppState.ProjectOnlySlot = tmpProjectOnly.length ? [ { Rows: tmpProjectOnly } ] : [];
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	/** Client-side filter of the workflow cards by hash (no re-render — just show/hide). */
	filterCards(pQuery)
	{
		const tmpQuery = String(pQuery || '').trim().toLowerCase();
		let tmpVisible = 0;
		Array.prototype.forEach.call(document.querySelectorAll('.apwl-row'), (pRow) =>
		{
			const tmpHash = (pRow.getAttribute('data-hash') || '').toLowerCase();
			const tmpMatch = !tmpQuery || tmpHash.indexOf(tmpQuery) > -1;
			pRow.style.display = tmpMatch ? '' : 'none';
			if (tmpMatch) { tmpVisible++; }
		});
		const tmpEmpty = document.getElementById('ApprovalWorkflow-List-NoMatch');
		if (tmpEmpty) { tmpEmpty.style.display = (tmpVisible === 0 && tmpQuery) ? '' : 'none'; }
	}

	_escapeHTML(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => (
			{ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
	}

	/** Suggest a free "<source>Copy" hash for the clone, bumping a numeric suffix past any collision. */
	_suggestCloneHash(pSourceHash)
	{
		const tmpBase = `${pSourceHash}Copy`;
		if (!this.provider.workflowHashExists(tmpBase)) { return tmpBase; }
		let tmpN = 2;
		while (this.provider.workflowHashExists(`${tmpBase}${tmpN}`)) { tmpN++; }
		return `${tmpBase}${tmpN}`;
	}

	/**
	 * Clone a workflow: prompt for a new name (the hash it is stored under), reject a name collision (a Put to an
	 * existing hash would overwrite it), copy the source blob verbatim, then open the copy in the editor.
	 */
	async cloneWorkflow(pSourceHash)
	{
		if (!this.provider || !this.provider.canEdit) { return; }
		const tmpModal = this.modal;
		const tmpChoice = await tmpModal.show(
			{
				title: 'Clone approval workflow',
				content: `<p>Copy <strong>${this._escapeHTML(pSourceHash)}</strong> to a new workflow. Enter a name (the key it is stored under):</p>`
					+ `<input type="text" id="apw-clone-hash" class="input" value="${this._escapeHTML(this._suggestCloneHash(pSourceHash))}" autofocus>`,
				buttons: [ { Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Clone', Style: 'primary' } ],
			});
		if (tmpChoice !== 'ok') { return; }
		const tmpNewHash = ((document.getElementById('apw-clone-hash') || {}).value || '').trim();
		if (!tmpNewHash) { return; }
		const tmpNameProblem = this.provider.nameProblem(tmpNewHash);
		if (tmpNameProblem)
		{
			tmpModal.toast(tmpNameProblem, { type: 'error', duration: 5000 });
			return;
		}
		if (tmpNewHash === pSourceHash) { tmpModal.toast('Choose a different name for the copy.', { type: 'warning' }); return; }
		if (this.provider.workflowHashExists(tmpNewHash))
		{
			tmpModal.toast(`A workflow named "${tmpNewHash}" already exists — choose a different name.`, { type: 'error', duration: 5000 });
			return;
		}
		try
		{
			await this.provider.cloneWorkflowAsync(pSourceHash, tmpNewHash);
			tmpModal.toast(`Cloned "${pSourceHash}" → "${tmpNewHash}".`, { type: 'success' });
			window.location.hash = `#/ApprovalWorkflow/${encodeURIComponent(tmpNewHash)}`;
		}
		catch (pError)
		{
			tmpModal.toast(`Clone failed: ${pError && pError.message ? pError.message : pError}`, { type: 'error', duration: 6000 });
		}
	}
}

module.exports = ApprovalWorkflowList;

module.exports.default_configuration =
{
	ViewIdentifier: 'ApprovalWorkflowList',
	DefaultRenderable: 'ApprovalWorkflowListWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
		.apwl-wrap { padding: 1.25rem 1.5rem 2rem; max-width: 78rem; }
		.apwl-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 0.5rem; }
		.apwl-count { display: inline-flex; align-items: center; font-size: 0.72rem; font-weight: 700; color: var(--bulma-link, #156dd1); background: #eef3fb; border-radius: 999px; padding: 0.1rem 0.6rem; margin-left: 0.5rem; vertical-align: middle; }
		.apwl-toolbar { margin: 0.85rem 0 1rem; max-width: 24rem; }
		.apwl-list { border: 1px solid var(--bulma-border, #e6e9ef); border-radius: 12px; background: var(--bulma-scheme-main, #fff); overflow: hidden; }
		.apwl-row { position: relative; display: flex; align-items: center; gap: 0.85rem; padding: 0.7rem 1rem; text-decoration: none; color: inherit; cursor: pointer;
			border-bottom: 1px solid var(--bulma-border, #f0f2f5); border-left: 3px solid transparent; transition: background 0.1s, border-left-color 0.1s; }
		.apwl-row:last-child { border-bottom: none; }
		.apwl-row:hover { background: var(--bulma-scheme-main-bis, #f6f8fc); border-left-color: var(--bulma-link, #156dd1); }
		.apwl-row-ic { flex-shrink: 0; color: var(--bulma-link, #156dd1); }
		.apwl-row-name { flex: 1; min-width: 0; font-weight: 700; color: #22334c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
		.apwl-row-meta { display: flex; gap: 0.4rem; flex-wrap: wrap; flex-shrink: 0; }
		.apwl-chip { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.72rem; font-weight: 600; padding: 0.15rem 0.55rem; border-radius: 999px; background: var(--bulma-scheme-main-bis, #f1f4f9); color: #5a6b82; }
		.apwl-chip .icon { font-size: 0.72rem; }
		.apwl-clone { flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; width: 1.9rem; height: 1.9rem; border-radius: 8px; color: #8494a8; cursor: pointer; transition: background 0.1s, color 0.1s; }
		.apwl-clone:hover { background: #eef3fb; color: var(--bulma-link, #156dd1); }
		.apwl-clone:focus-visible { outline: 2px solid var(--bulma-link, #156dd1); outline-offset: 1px; }
		.apwl-row-arrow { flex-shrink: 0; color: var(--bulma-border, #cfd6e2); transition: color 0.12s, transform 0.12s; }
		.apwl-row:hover .apwl-row-arrow { color: var(--bulma-link, #156dd1); transform: translateX(2px); }
		.apwl-empty { padding: 2.5rem 1rem; }
		.apwl-spinner { display: inline-block; width: 1.6rem; height: 1.6rem; border: 3px solid var(--bulma-border, #dbdbdb);
			border-top-color: var(--bulma-link, #156dd1); border-radius: 50%; animation: apwl-spin 0.8s linear infinite; }
		@keyframes apwl-spin { to { transform: rotate(360deg); } }
	`,

	Templates:
	[
		{
			Hash: 'ApprovalWorkflow-List-Wrap',
			Template: /*html*/`
				<div class="apwl-wrap">
					<div class="apwl-header">
						<div>
							<h1 class="title is-4"><span class="icon"><i class="fas fa-diagram-project"></i></span> Approval Workflows{~TS:ApprovalWorkflow-List-Count:AppData.ApprovalWorkflowEditor.CountSlot~} <span class="apw-help" role="button" tabindex="0" title="What are approval workflows?" onclick="ApprovalWorkflowHelp.show('list.overview', this)"><i class="fas fa-circle-info"></i></span></h1>
							<p class="subtitle is-6 has-text-grey">The state-transition workflows that govern how documents and observations move between approval statuses. Open one to edit it as a visual graph.</p>
						</div>
						<div class="field is-grouped">
							<p class="control"><a class="button is-light" href="#/ApprovalWorkflows/Bindings"><span class="icon"><i class="fas fa-file-lines"></i></span><span>Document bindings</span></a></p>
							<p class="control"><a class="button is-light" href="#/ApprovalWorkflows/Bindings/Observation"><span class="icon"><i class="fas fa-clipboard-check"></i></span><span>Observation bindings</span></a></p>
							{~TS:ApprovalWorkflow-List-NewButton:AppData.ApprovalWorkflowEditor.NewButtonSlot~}
						</div>
					</div>

					{~TS:ApprovalWorkflow-List-Error:AppData.ApprovalWorkflowEditor.ErrorSlot~}

					<div class="apwl-toolbar">
						<div class="control has-icons-left">
							<input class="input" type="text" placeholder="Filter workflows…" oninput="_Pict.views.ApprovalWorkflowList.filterCards(this.value)">
							<span class="icon is-left"><i class="fas fa-magnifying-glass"></i></span>
						</div>
					</div>

					<div class="apwl-list">
						{~TS:ApprovalWorkflow-List-Loading:AppData.ApprovalWorkflowEditor.LoadingSlot~}
						{~TS:ApprovalWorkflow-List-Card:AppData.ApprovalWorkflowEditor.List~}
						<div id="ApprovalWorkflow-List-NoMatch" class="apwl-empty has-text-grey has-text-centered" style="display:none"><span class="icon is-large"><i class="fas fa-magnifying-glass fa-lg"></i></span><p class="mt-2">No workflows match your filter.</p></div>
						{~TS:ApprovalWorkflow-List-Empty:AppData.ApprovalWorkflowEditor.EmptySlot~}
					</div>
					{~TS:ApprovalWorkflow-List-ProjectOnly:AppData.ApprovalWorkflowEditor.ProjectOnlySlot~}
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-List-NewButton',
			Template: /*html*/`<button class="button is-link" onclick="window.location.hash = '#/ApprovalWorkflow/New'"><span class="icon"><i class="fas fa-plus"></i></span><span>New workflow</span></button>`
		},
		{
			Hash: 'ApprovalWorkflow-List-Count',
			Template: /*html*/`<span class="apwl-count">{~D:Record.Count~}</span>`
		},
		{
			Hash: 'ApprovalWorkflow-List-Card',
			Template: /*html*/`
				<a class="apwl-row" href="#/ApprovalWorkflow/{~D:Record.EncodedHash~}" data-hash="{~D:Record.Hash~}">
					<span class="icon apwl-row-ic"><i class="fas fa-diagram-project"></i></span>
					<span class="apwl-row-name">{~D:Record.Hash~}</span>
					<span class="apwl-row-meta">
						<span class="apwl-chip"><span class="icon"><i class="fas fa-circle-nodes"></i></span> {~D:Record.StateCount~} states</span>
						<span class="apwl-chip"><span class="icon"><i class="fas fa-arrow-right-arrow-left"></i></span> {~D:Record.TransitionCount~} transitions</span>
						{~TS:ApprovalWorkflow-List-Level:Record.LevelSlot~}
					</span>
					{~TS:ApprovalWorkflow-List-CloneControl:Record.CloneControlSlot~}
					<span class="icon apwl-row-arrow"><i class="fas fa-chevron-right"></i></span>
				</a>`
		},
		{
			Hash: 'ApprovalWorkflow-List-Level',
			Template: /*html*/`<span class="apwl-chip"><span class="icon"><i class="fas fa-layer-group"></i></span> {~D:Record.Text~}</span>`
		},
		{
			Hash: 'ApprovalWorkflow-List-ProjectOnly',
			Template: /*html*/`
				<h2 class="title is-6 mt-5 mb-2">Set only on a project</h2>
				<div class="apwl-list">{~TS:ApprovalWorkflow-List-ProjectOnlyRow:Record.Rows~}</div>`
		},
		{
			Hash: 'ApprovalWorkflow-List-ProjectOnlyRow',
			Template: /*html*/`
				<a class="apwl-project-row apwl-row" href="{~D:Record.Href~}" data-hash="{~D:Record.Hash~}">
					<span class="icon apwl-row-ic"><i class="fas fa-layer-group"></i></span>
					<span class="apwl-row-name">{~D:Record.Hash~}</span>
					<span class="apwl-row-meta"><span class="apwl-chip">{~D:Record.ProjectName~}</span></span>
					<span class="icon apwl-row-arrow"><i class="fas fa-chevron-right"></i></span>
				</a>`
		},
		{
			Hash: 'ApprovalWorkflow-List-CloneControl',
			Template: /*html*/`<span class="apwl-clone" role="button" tabindex="0" title="Clone this workflow" data-hash="{~D:Record.Hash~}" onclick="event.preventDefault();event.stopPropagation();_Pict.views.ApprovalWorkflowList.cloneWorkflow(this.dataset.hash);return false;"><span class="icon"><i class="fas fa-clone"></i></span></span>`
		},
		{
			Hash: 'ApprovalWorkflow-List-Empty',
			Template: /*html*/`
				<div class="apwl-empty has-text-grey has-text-centered">
					<span class="icon is-large"><i class="fas fa-diagram-project fa-2x"></i></span>
					<p class="mt-3">No approval workflows are configured for your organization yet.</p>
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-List-Loading',
			Template: /*html*/`<div class="apwl-empty has-text-grey has-text-centered"><span class="apwl-spinner"></span><p class="mt-3">Loading approval workflows…</p></div>`
		},
		{
			Hash: 'ApprovalWorkflow-List-Error',
			Template: /*html*/`<div class="notification is-danger is-soft py-2 px-3 mb-3"><span class="icon"><i class="fas fa-triangle-exclamation"></i></span> <span>{~D:Record.Message~}</span></div>`
		}
	],

	Renderables:
	[
		{
			RenderableHash: 'ApprovalWorkflowListWrap',
			TemplateHash: 'ApprovalWorkflow-List-Wrap',
			ContentDestinationAddress: '#PlatformConfiguration-Application-Container'
		}
	]
};
